import { GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';
import type { ChildGiftLimitItem } from '@sola/schema/dotcom/proxy-api/giftLimits.ts';
import { GiftLimitReason } from '@sola/schema/dotcom/shared/gifts.ts';
import type {
  GraphQLSupporterGiftLimits,
  GraphQLSupporterGiftTypeLimits,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import {
  FinalGiftIneligibilityCode,
  isBirthdayGift,
  isCenterGift,
  isChildGift,
  isFamilyGift,
  isFinalGift,
} from '@sola/shared-utils/my/gifts-type-guards.ts';

import type { GiftOption } from '../../types/gifts.ts';
import {
  BadgeLabel,
  ButtonAction,
  GiftTooltip,
  ProgressLabelKey,
} from '../../types/gifts.ts';

const isExhausted = (value: number) => value === 0;

const isAmountBased = (giftType: GiftTypeEnum) => isFamilyGift(giftType);

const progressLabelKey = (giftType: GiftTypeEnum, max: number) => {
  if (isFinalGift(giftType)) {
    return ProgressLabelKey.Final;
  }

  if (isAmountBased(giftType)) {
    return ProgressLabelKey.Family;
  }

  return ProgressLabelKey.GiftsPlural;
};

type ResolveContext = {
  giftType: GiftTypeEnum;
  limits: ChildGiftLimitItem;
  defaults: ChildGiftLimitItem;
  allLimits: GraphQLSupporterGiftTypeLimits;
  allDefaults: GraphQLSupporterGiftLimits['defaults'];
  given: number;
  max: number;
};

/**
 * NOTE: Closes over the shared base properties (cart, payment options) so each rule
 * only needs to specify the properties that vary per scenario.
 */
const createGiftView =
  <B extends Partial<GiftOption>>(base: B) =>
  (overrides: Omit<GiftOption, keyof B>): GiftOption =>
    ({ ...base, ...overrides }) as GiftOption;

type GiftRule = {
  match: (giftContext: ResolveContext) => boolean;
  resolve: (
    giftContext: ResolveContext,
    buildOption: (
      overrides: Omit<
        GiftOption,
        'cartItem' | 'paymentOptions' | 'paymentOptionsMinMax'
      >,
    ) => GiftOption,
  ) => GiftOption;
};

/**
 * NOTE: Detects when a recurring birthday is already active for this child.
 * In that case the API decrements child remainingQuantity to reserve a slot
 * for the upcoming birthday pull — but no child gift was actually sent.
 */
const isBirthdayRecurringActive = (
  allLimits: GraphQLSupporterGiftTypeLimits,
): boolean => {
  const birthdayLimits = allLimits[GiftTypeEnum.Birthday];
  return (
    !!birthdayLimits &&
    isExhausted(birthdayLimits.remainingQuantity) &&
    !birthdayLimits.paymentOptions.annual
  );
};

const giftRules: GiftRule[] = [
  /** Final: already given — informational state, no button, shows given progress */
  {
    match: ({ giftType, limits }) =>
      isFinalGift(giftType) &&
      limits.ineligibilityCode === FinalGiftIneligibilityCode.alreadyGiven,
    resolve: ({ giftType, limits, defaults }, buildOption) =>
      buildOption({
        canGive: false,
        buttonAction: null,
        progressLabel: {
          given: defaults.remainingQuantity - limits.remainingQuantity,
          max: defaults.remainingQuantity,
          labelKey: progressLabelKey(giftType, defaults.remainingQuantity),
        },
        tooltip: { key: GiftTooltip.FinalGiftAlreadyGiven },
        badge: null,
      }),
  },

  /** Final: eligible — Final gifts do not use quantity/amount progress */
  {
    match: ({ giftType }) => isFinalGift(giftType),
    resolve: (_giftContext, buildOption) =>
      buildOption({
        canGive: true,
        buttonAction: ButtonAction.AddGift,
        progressLabel: null,
        tooltip: null,
        badge: null,
      }),
  },

  /** Birthday: recurring already active */
  {
    match: ({ giftType, allLimits }) =>
      isBirthdayGift(giftType) && isBirthdayRecurringActive(allLimits),
    resolve: (_giftContext, buildOption) =>
      buildOption({
        canGive: false,
        buttonAction: null,
        progressLabel: null,
        tooltip: { key: GiftTooltip.RecurringGivingSetup },
        badge: {
          label: BadgeLabel.RecurringGiving,
          color: 'positive',
          icon: 'check-circle',
        },
      }),
  },

  /** Birthday: maxed but recurring available (child gifts may have caused the cap) */
  {
    match: ({ giftType, limits }) =>
      isBirthdayGift(giftType) &&
      isExhausted(limits.remainingQuantity) &&
      limits.paymentOptions.annual,
    resolve: ({ allLimits, allDefaults }, buildOption) => {
      const childLimits = allLimits[GiftTypeEnum.Child];
      const childDefaults = allDefaults[GiftTypeEnum.Child];
      const birthdayDefaults = allDefaults[GiftTypeEnum.Birthday]!;
      const birthdayLimits = allLimits[GiftTypeEnum.Birthday]!;

      // isMaxedByChildGifts returns true if two Child gifts and no
      // Birthday gifts have been given, which prompts the AlreadyGivenMaxTimesChild
      // error code for birthday gifts.
      // This is to differentiate between, for example 1 bday, 1 child vs. 0 bday, 2 child
      const isMaxedByChildGifts =
        childLimits &&
        childDefaults &&
        isExhausted(childLimits.remainingQuantity) &&
        birthdayLimits.ineligibilityCode ===
          GiftLimitReason.AlreadyGivenMaxTimesChild;

      return buildOption({
        canGive: false,
        buttonAction: ButtonAction.SetupRecurring,
        progressLabel: isMaxedByChildGifts
          ? {
              given:
                childDefaults.remainingQuantity - childLimits.remainingQuantity,
              max: childDefaults.remainingQuantity,
              labelKey: ProgressLabelKey.ChildGiftsGiven,
            }
          : {
              given:
                birthdayDefaults.remainingQuantity -
                birthdayLimits.remainingQuantity,
              max: birthdayDefaults.remainingQuantity,
              labelKey: progressLabelKey(
                GiftTypeEnum.Birthday,
                birthdayDefaults.remainingQuantity,
              ),
            },
        tooltip: isMaxedByChildGifts
          ? { key: GiftTooltip.BirthdayGiftMaxed }
          : { key: GiftTooltip.BirthdayGiftGiven },
        badge: null,
      });
    },
  },
  /** Birthday <-> Child combo: both given for the same child */
  {
    match: ({ giftType, allLimits, allDefaults }) => {
      //Birthday maxed (recurring available) covers all the maxed birthday card scenario described here
      //So, this rule is only really affecting child gift cards.
      if (!isBirthdayGift(giftType) && !isChildGift(giftType)) {
        return false;
      }

      // If birthday recurring is already active, the child remaining was
      // reduced by the system to reserve the birthday slot — not because a
      // child gift was sent.
      // Skip the combo rule in that case.
      if (isBirthdayRecurringActive(allLimits)) {
        return false;
      }

      const birthdayLimits = allLimits[GiftTypeEnum.Birthday];
      const birthdayDefaults = allDefaults[GiftTypeEnum.Birthday];
      const childLimits = allLimits[GiftTypeEnum.Child];
      const childDefaults = allDefaults[GiftTypeEnum.Child];

      if (
        !birthdayLimits ||
        !birthdayDefaults ||
        !childLimits ||
        !childDefaults
      ) {
        return false;
      }
      const isMaxedByChildGifts =
        childLimits &&
        childDefaults &&
        birthdayLimits.ineligibilityCode ===
          GiftLimitReason.AlreadyGivenMaxTimesChild;

      const birthdayGiven =
        birthdayDefaults.remainingQuantity - birthdayLimits.remainingQuantity;
      return (
        birthdayGiven >= 1 &&
        !isMaxedByChildGifts &&
        isExhausted(childLimits.remainingQuantity)
      );
    },
    /**
     * NOTE: The `!` non-null assertion is safe here, the match function above guarantees all four lookups are defined before resolve runs
     */
    resolve: ({ allLimits, allDefaults }, buildOption) => {
      const birthdayDefaults = allDefaults[GiftTypeEnum.Birthday]!;
      const birthdayLimits = allLimits[GiftTypeEnum.Birthday]!;
      const childDefaults = allDefaults[GiftTypeEnum.Child]!;
      const childLimits = allLimits[GiftTypeEnum.Child]!;
      const birthdayGiven =
        birthdayDefaults.remainingQuantity - birthdayLimits.remainingQuantity;

      // Adding birthdayGiven is to account for the API decrement that takes
      // place before the logic. When a bday gift is given, the API automatically
      // reduces childLimits.remainingQuantity
      const childGiven =
        childDefaults.remainingQuantity -
        (childLimits.remainingQuantity + birthdayGiven);
      const effectiveChildMax = childDefaults.remainingQuantity - birthdayGiven;

      return buildOption({
        canGive: false,
        buttonAction: null,
        progressLabel: {
          given: childGiven,
          max: effectiveChildMax,
          labelKey: progressLabelKey(GiftTypeEnum.Child, effectiveChildMax),
        },
        tooltip: { key: GiftTooltip.ChildGiftAndBirthdayGiven },
        badge: null,
      });
    },
  },

  /** Child: quantity maxed */
  {
    match: ({ giftType, limits }) =>
      isChildGift(giftType) && isExhausted(limits.remainingQuantity),
    resolve: ({ giftType, given, max }, buildOption) =>
      buildOption({
        canGive: false,
        buttonAction: null,
        progressLabel: {
          given,
          max,
          labelKey: progressLabelKey(giftType, max),
        },
        tooltip: { key: GiftTooltip.ChildGiftMaxed },
        badge: null,
      }),
  },

  /** Family: amount maxed */
  {
    match: ({ giftType, limits }) =>
      isFamilyGift(giftType) && isExhausted(limits.remainingAmount),
    resolve: ({ giftType, given, max }, buildOption) =>
      buildOption({
        canGive: false,
        buttonAction: null,
        progressLabel: {
          given,
          max,
          labelKey: progressLabelKey(giftType, max),
        },
        tooltip: { key: GiftTooltip.FamilyGiftAmountMaxed },
        badge: null,
      }),
  },

  /** Center: amount maxed */
  {
    match: ({ giftType, limits }) =>
      isCenterGift(giftType) && isExhausted(limits.remainingAmount),
    resolve: ({ limits, defaults }, buildOption) =>
      buildOption({
        canGive: false,
        buttonAction: null,
        progressLabel: {
          given: defaults.remainingAmount - limits.remainingAmount,
          max: defaults.remainingAmount,
          labelKey: ProgressLabelKey.Family,
        },
        tooltip: { key: GiftTooltip.CenterGiftAmountMaxed },
        badge: null,
      }),
  },

  /** Center: quantity maxed */
  {
    match: ({ giftType, limits }) =>
      isCenterGift(giftType) && isExhausted(limits.remainingQuantity),
    resolve: ({ limits, defaults }, buildOption) =>
      buildOption({
        canGive: false,
        buttonAction: null,
        progressLabel: {
          given: defaults.remainingQuantity - limits.remainingQuantity,
          max: defaults.remainingQuantity,
          labelKey: ProgressLabelKey.GiftsPlural,
        },
        tooltip: { key: GiftTooltip.CenterGiftLimitMaxed },
        badge: null,
      }),
  },

  /** Family: quantity maxed */
  {
    match: ({ giftType, limits }) =>
      isFamilyGift(giftType) && isExhausted(limits.remainingQuantity),
    resolve: ({ limits, defaults }, buildOption) =>
      buildOption({
        canGive: false,
        buttonAction: null,
        progressLabel: {
          given: defaults.remainingQuantity - limits.remainingQuantity,
          max: defaults.remainingQuantity,
          labelKey: ProgressLabelKey.GiftsPlural,
        },
        tooltip: { key: GiftTooltip.FamilyGiftQuantityMaxed },
        badge: null,
      }),
  },

  /** Partially given (any type) */
  {
    match: ({ given }) => given >= 1,
    resolve: ({ giftType, given, max }, buildOption) =>
      buildOption({
        canGive: true,
        buttonAction: ButtonAction.GiveAnother,
        progressLabel: {
          given,
          max,
          labelKey: progressLabelKey(giftType, max),
        },
        tooltip: {
          key: isAmountBased(giftType)
            ? GiftTooltip.FamilyGiftBelowLimit
            : GiftTooltip.GiftBelowLimit,
        },
        badge: null,
      }),
  },
];

export const resolveGiftOption = (
  giftType: GiftTypeEnum,
  limits: ChildGiftLimitItem,
  defaults: ChildGiftLimitItem,
  allLimits: GraphQLSupporterGiftTypeLimits,
  allDefaults: GraphQLSupporterGiftLimits['defaults'],
): GiftOption => {
  /** NOTE: determine if this gift max/given is based on dollar amounts or quantity of gifts: */
  const rawMax = isAmountBased(giftType)
    ? defaults.remainingAmount
    : defaults.remainingQuantity;
  const rawGiven = isAmountBased(giftType)
    ? rawMax - limits.remainingAmount
    : rawMax - limits.remainingQuantity;

  /**
   * NOTE: When a recurring birthday is active and we're evaluating a child gift,
   * the API has already decremented child remaining to reserve the birthday slot.
   * Adjust max/given so the UI reflects only actual child gifts sent.
   * This will only impact Child and Birthday gifts and only quantity for those.
   */
  const adjustForBirthdayReservation =
    isChildGift(giftType) && isBirthdayRecurringActive(allLimits);

  /**
   * NOTE: The 1 here is hardcoded because of the way that the API returns
   * the child gift already accounting for a recurring birthday gift.
   */
  const max = Math.max(0, adjustForBirthdayReservation ? rawMax - 1 : rawMax);
  const given = Math.max(
    0,
    adjustForBirthdayReservation ? rawGiven - 1 : rawGiven,
  );

  const buildGiftOption = createGiftView({
    cartItem: null,
    paymentOptions: limits.paymentOptions,
    paymentOptionsMinMax: limits.paymentOptionsMinMax,
  });

  const giftContext: ResolveContext = {
    giftType,
    limits,
    defaults,
    allLimits,
    allDefaults,
    given,
    max,
  };

  const matched = giftRules.find((rule) => rule.match(giftContext));
  if (matched) {
    return matched.resolve(giftContext, buildGiftOption);
  }

  /** NOTE: Default: available (not given yet) */
  return buildGiftOption({
    canGive: true,
    buttonAction: ButtonAction.AddGift,
    progressLabel: null,
    tooltip: null,
    badge: null,
  });
};
